import { useEffect } from "react";
import { useMap } from "react-leaflet";
import type { Layer } from "leaflet";

function closeAllTooltips(layer: Layer): void {
  (layer as Layer & { closeTooltip?: () => void }).closeTooltip?.();
  (layer as Layer & { eachLayer?: (fn: (l: Layer) => void) => void }).eachLayer?.(closeAllTooltips);
}

/**
 * Two independent real bugs, both traced to the same root cause and fixed
 * with the same sweep — reported live, and both reproduced/confirmed with
 * Playwright before writing this (real mouse hover, real remount trigger,
 * no synthetic event faking beyond simulating `visibilitychange` itself):
 *
 * 1. Minimize the window (or switch tabs) while hovering a sub-basin, then
 *    restore it: the tooltip is still there, and moving the mouse to a
 *    DIFFERENT sub-basin opens a second one without closing the first. The
 *    page stops receiving input entirely while hidden, so the browser never
 *    fires that layer's `mouseout` — Leaflet's sticky tooltip (bound via
 *    `layer.bindTooltip(...)`, see SubBasinMap.tsx's `onEachFeature`) only
 *    closes on ITS OWN layer's mouseout, so it never learns to close.
 *
 * 2. A tooltip gets stuck EVEN WITHOUT ever minimizing/switching tabs, as
 *    long as the sub-basins GeoJSON layer remounts while that tooltip is
 *    open. SubBasinMap.tsx forces a remount (`key={signature}`) any time
 *    something meaningful about a sub-basin changes — entering/leaving
 *    edit/results/series mode, opening an edit dialog, a zone/outlet/
 *    aquifer assignment changing, OR (the case that actually surfaced this
 *    live) a MITECO/ROCIO bulk import applying data to each sub-basin one
 *    by one, each one flipping that sub-basin's own "problems" and so
 *    changing `signature`. When React destroys the old GeoJSON layer,
 *    Leaflet's own teardown (`FeatureGroup.onRemove` → `map.removeLayer`
 *    on each child → that child's own `'remove'` event → `closeTooltip()`)
 *    is SUPPOSED to close any tooltip bound to it — confirmed by reading
 *    Leaflet's own source that this wiring exists — but empirically, doing
 *    exactly that (hover a sub-basin, then force a remount via a
 *    `signature`-changing state change WITHOUT moving the mouse) leaves the
 *    tooltip visibly stuck anyway, unreachable by any further mouse event
 *    on the new layer (it's a different object; the new polygon at the
 *    same screen position never got a real mouseover, since the mouse
 *    never moved) — orphaned, but still reachable via `map.eachLayer` (the
 *    tooltip overlay itself is added directly to the map, not nested
 *    inside the sub-basins FeatureGroup), which is exactly what the sweep
 *    below uses to reach and close it.
 *
 * Fix: force-close every currently open tooltip on the map (walking
 * recursively — `map.eachLayer` only covers TOP-level layers, not each
 * individual feature inside a GeoJSON/FeatureGroup) on two triggers:
 * regaining visibility (case 1), and whenever `remountKey` changes (case 2)
 * — pass the SAME value SubBasinMap.tsx uses as the sub-basins layer's own
 * `key` so this runs right after every remount, within one render instead
 * of waiting for the next minimize/tab-switch to clean it up.
 */
export function TooltipVisibilityFix({ remountKey }: { remountKey?: unknown } = {}) {
  const map = useMap();

  useEffect(() => {
    function onVisibilityChange(): void {
      if (document.visibilityState === "visible") map.eachLayer(closeAllTooltips);
    }
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => document.removeEventListener("visibilitychange", onVisibilityChange);
  }, [map]);

  // `remountKey` is a proxy for "the layer this map holds just got rebuilt"
  // — not read by the effect body, just watched so this re-runs each time.
  useEffect(() => {
    map.eachLayer(closeAllTooltips);
  }, [map, remountKey]);

  return null;
}
